// The AFK rule (2026-09-11). A held seat costs GPU money whether or not a human is in front of it, and a
// forgotten tab is the cheapest way there is to burn a box: the page renews its own lease for as long as
// the browser lives. So a page that has seen no human gesture for an hour asks the question out loud, and
// hands the seat back if nobody answers.
//
// Client-side on purpose, even though myta-streams already carries a server-authoritative idle rule
// (`infra/lambdas/api/seat-policy.ts` → 403 session_ended, cause `idle`). Two things the server cannot do
// from where it sits:
//   - it ships in `shadow` mode (the dev pool's seatPolicy, read 2026-09-11) — it computes every verdict
//     and refuses nothing, so a UI driven only off its `warn` flag would be dead code today;
//   - the renew cadence is 45 s and its warn window is 60 s, so a server-timed prompt would reach the user
//     with anywhere between 15 and 60 s left on it. A minute we promise has to BE a minute, and only the
//     page that owns the input clock can time it.
// The server rule stays the backstop underneath: it is what protects a seat from a client that does not
// implement this one — an old hub, a page killed mid-visit, a laptop lid closed on a hidden tab.
//
// The clock read here is the SAME `lastInputAt` the renew reports, so the question is asked about exactly
// the fact the seat policy judges on, and answering it moves both clocks at once.

export const AFK_IDLE_MS = 60 * 60 * 1000;

export const AFK_ANSWER_MS = 60 * 1000;

// How often the idle clock is looked at. Coarse on purpose — it only has to be fine enough that the prompt
// is not visibly late, because the answer window itself is a timer rather than a poll.
export const AFK_POLL_MS = 5000;

export interface IAfkPrompt {
  raisedAt: number;
  deadline: number;
}

export const isAfk = (lastInputAt: number, now: number): boolean =>
  now - lastInputAt >= AFK_IDLE_MS;

export const afkPromptAt = (now: number): IAfkPrompt => ({
  raisedAt: now,
  deadline: now + AFK_ANSWER_MS,
});

export const afkSecondsLeft = (prompt: IAfkPrompt, now: number): number =>
  Math.max(0, Math.ceil((prompt.deadline - now) / 1000));

export const afkFractionLeft = (prompt: IAfkPrompt, now: number): number =>
  Math.max(0, Math.min(1, (prompt.deadline - now) / AFK_ANSWER_MS));

const minutes = (value: number): string => `${Math.round(value / 60000)} min`;

export const AFK_ENDED_WHY = `no input for ${minutes(
  AFK_IDLE_MS,
)} and the presence check went unanswered for ${AFK_ANSWER_MS / 1000} s`;

export const AFK_TITLE = 'Are you still there?';

export const AFK_BODY = `We have not seen any activity here for ${minutes(
  AFK_IDLE_MS,
)}. This experience runs on a GPU held just for you, so sessions nobody is using get closed.`;

export const AFK_FOOTER =
  'Confirm you are here and you will carry on exactly where you left off.';

export const AFK_CONFIRM = 'I am still here';

export const SESSION_ENDED_TITLE = 'Your session has ended';

// One end is not an ending: when the page gives up on getting the user IN (entryWatch.ts), calling that "your
// session has ended" would blame the visit for a failure that was ours, before the visit ever started.
const SESSION_ENDED_TITLES: Record<string, string> = {
  'entry-stalled': 'We could not get you in',
};

export const sessionEndTitle = (cause: string): string =>
  SESSION_ENDED_TITLES[cause] ?? SESSION_ENDED_TITLE;

const SESSION_ENDED_BODY: Record<string, string> = {
  idle: 'It was closed because nobody was using it.',
  'max-session': 'It reached the time limit for a single visit.',
  hidden: 'It was closed because this tab spent too long in the background.',
  // The one cause that is not about the user at all — so it says whose fault it is, and that retrying works.
  'entry-stalled':
    'Every machine we tried started streaming but never finished loading your space, so we stopped rather than leave you watching a loading screen. This is on our side, not your connection — starting again usually works.',
};

const SESSION_ENDED_TAIL =
  'Taking you back to the events list — you can start a new session whenever you like.';

// Every end cause gets a sentence, including the ones this page does not raise itself: the same dialog
// covers the server's 403 (seat cap, operator kick), which until now ended a visit with no explanation at
// all — six seconds of frozen last frame and then the events list.
export const describeSessionEnd = (cause: string): string =>
  `${SESSION_ENDED_BODY[cause] ?? ''} ${SESSION_ENDED_TAIL}`.trim();
